class TestimonialsCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.testimonials-track');
    this.prevBtn = this.querySelector('.testimonials-arrow--prev');
    this.nextBtn = this.querySelector('.testimonials-arrow--next');
    this.dotsWrap = this.querySelector('.testimonials-dots');
    this.cards = Array.from(this.querySelectorAll('.testimonial-card'));
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    this.timer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.track || this.cards.length === 0) return;

    this.buildDots();
    this.bindEvents();
    this.updateArrowState();
    this.observeActiveCard();

    if (this.autoplay && !this.reducedMotion) {
      this.startAutoplay();
    }
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.cards.forEach((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonials-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to testimonial ${i + 1}`);
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => this.scrollToIndex(i));
      this.dotsWrap.appendChild(dot);
    });
    this.dots = Array.from(this.dotsWrap.children);
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByCards(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByCards(1));
    }

    this.track.addEventListener('scroll', () => this.updateArrowState(), { passive: true });

    // Pause autoplay on interaction
    ['pointerdown', 'touchstart', 'mouseenter'].forEach((evt) => {
      this.addEventListener(evt, () => this.stopAutoplay());
    });
    this.addEventListener('mouseleave', () => {
      if (this.autoplay && !this.reducedMotion) this.startAutoplay();
    });

    // Keyboard support
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.scrollByCards(1);
      if (e.key === 'ArrowLeft') this.scrollByCards(-1);
    });

    // Sync when editing in the Shopify theme editor
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.contains(this)) {
        this.cards = Array.from(this.querySelectorAll('.testimonial-card'));
      }
    });

    document.addEventListener('shopify:block:select', (e) => {
      const card = this.cards.find((c) => c.contains(e.target) || c === e.target);
      if (card) this.scrollToCard(card);
    });
  }

  getCardStep() {
    const card = this.cards[0];
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap || 0);
    return card.getBoundingClientRect().width + gap;
  }

  scrollByCards(direction) {
    const step = this.getCardStep();
    this.track.scrollBy({ left: step * direction, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  scrollToIndex(index) {
    this.scrollToCard(this.cards[index]);
  }

  scrollToCard(card) {
    if (!card) return;
    this.track.scrollTo({
      left: card.offsetLeft - this.track.offsetLeft,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  updateArrowState() {
    if (!this.prevBtn && !this.nextBtn) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
  }

  observeActiveCard() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const index = this.cards.indexOf(entry.target);
            this.setActiveDot(index);
          }
        });
      },
      { root: this.track, threshold: [0.6] }
    );
    this.cards.forEach((card) => observer.observe(card));
  }

  setActiveDot(index) {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.track.scrollLeft >= maxScroll) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.scrollByCards(1);
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

customElements.define('testimonials-carousel', TestimonialsCarousel);